import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Printer, RefreshCw, Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import printerService from '../../services/printer/printerService';
import PosDiagnosticsPanel from './PosDiagnosticsPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * POS Printer tab — status probe + Test Print + Auto-print toggle.
 *
 * Deliberately minimal. Only the printer_mode="android_builtin" case is
 * exposed here because the POS device HAS the built-in printer. Advanced
 * settings (network printer IP, QZ Tray, etc.) stay on /admin/printer-settings.
 */
export default function PosPrinterPage() {
  const [status, setStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [testing, setTesting] = useState(false);
  const [settings, setSettings] = useState(null);
  const [savingToggle, setSavingToggle] = useState(false);

  const refreshStatus = useCallback(async () => {
    setLoadingStatus(true);
    try {
      const s = await printerService.getStatus();
      setStatus(s);
    } catch (err) {
      setStatus({ available: false, error: String(err?.message || err) });
    } finally {
      setLoadingStatus(false);
    }
  }, []);

  const loadSettings = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const res = await axios.get(`${API_URL}/api/admin/printer-settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSettings(res.data);
    } catch (_e) {
      setSettings({ auto_print: true, printer_mode: 'android_builtin' });
    }
  }, []);

  useEffect(() => {
    refreshStatus();
    loadSettings();
  }, [refreshStatus, loadSettings]);

  const doTestPrint = async () => {
    setTesting(true);
    try {
      const res = await printerService.testPrint();
      if (res?.success) toast.success('Test receipt sent');
      else toast.error(`Test failed: ${res?.error || 'unknown'}`);
    } finally {
      setTesting(false);
    }
  };

  const toggleAutoPrint = async () => {
    if (!settings) return;
    const next = !settings.auto_print;
    setSavingToggle(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/admin/printer-settings`,
        {
          printer_name: settings.printer_name || null,
          auto_print: next,
          printer_mode: settings.printer_mode || 'android_builtin',
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setSettings((s) => ({ ...s, auto_print: next }));
      toast.success(next ? 'Auto-print enabled' : 'Auto-print disabled');
    } catch (_e) {
      toast.error('Failed to save');
    } finally {
      setSavingToggle(false);
    }
  };

  const available = status?.available;

  return (
    <div className="p-4 space-y-4" data-testid="pos-printer-page">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold">Printer</h2>
        <button
          type="button"
          onClick={refreshStatus}
          className="p-2 rounded-full hover:bg-neutral-200 active:bg-neutral-300"
          data-testid="pos-printer-refresh"
          aria-label="Refresh status"
        >
          <RefreshCw className={`h-5 w-5 ${loadingStatus ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Status card */}
      <div
        className={`rounded-xl border p-4 ${
          available === true
            ? 'border-emerald-200 bg-emerald-50'
            : available === false
            ? 'border-red-200 bg-red-50'
            : 'border-neutral-200 bg-white'
        }`}
        data-testid="pos-printer-status-card"
      >
        <div className="flex items-center gap-3">
          {available === true ? (
            <CheckCircle2 className="h-6 w-6 text-emerald-600" />
          ) : available === false ? (
            <AlertTriangle className="h-6 w-6 text-red-600" />
          ) : (
            <Loader2 className="h-6 w-6 animate-spin text-neutral-400" />
          )}
          <div>
            <div className="font-semibold" data-testid="pos-printer-status-label">
              {available === true ? 'Printer ready' : available === false ? 'Printer unavailable' : 'Checking…'}
            </div>
            <div className="text-sm text-neutral-600">
              {status?.deviceName || 'Built-in POS printer'}
            </div>
            {status?.error && (
              <div className="text-xs text-red-700 mt-1">{status.error}</div>
            )}
          </div>
        </div>
      </div>

      {/* Test print */}
      <button
        type="button"
        onClick={doTestPrint}
        disabled={testing || available !== true}
        className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-neutral-900 text-white font-medium disabled:opacity-40 active:bg-neutral-700"
        data-testid="pos-printer-test-btn"
      >
        {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4" />}
        Print test receipt
      </button>

      {/* Auto-print toggle */}
      <div className="rounded-xl border border-neutral-200 bg-white p-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-semibold">Auto-print new orders</div>
            <div className="text-xs text-neutral-500 mt-0.5">
              Prints receipts automatically as orders arrive.
            </div>
          </div>
          <button
            type="button"
            onClick={toggleAutoPrint}
            disabled={savingToggle || !settings}
            className={`relative h-7 w-12 rounded-full transition-colors ${
              settings?.auto_print ? 'bg-emerald-500' : 'bg-neutral-300'
            } disabled:opacity-40`}
            data-testid="pos-auto-print-toggle"
            aria-pressed={!!settings?.auto_print}
          >
            <span
              className={`absolute top-0.5 h-6 w-6 bg-white rounded-full shadow transition-transform ${
                settings?.auto_print ? 'translate-x-5' : 'translate-x-0.5'
              }`}
            />
          </button>
        </div>
      </div>

      {/* Live diagnostics — collapsed by default. Helps triage POS reliability. */}
      <PosDiagnosticsPanel />
    </div>
  );
}
